import React, { useState, useCallback, useMemo, useEffect } from 'react';

import { Analytics } from '@trello/atlassian-analytics';
import { useSharedState } from '@trello/shared-state';

import { ErrorBoundary } from 'app/src/components/ErrorBoundary';
import { Feature } from 'app/scripts/debug/constants';
import { ResolveResponse } from 'app/scripts/network/object-resolver-client/types';
import { smartCardClient } from 'app/scripts/network/smart-card-client';

import { AkSmartCardWithProvider } from './AkSmartCardWithProvider';
import { SmartLinkAnalyticsContextType } from './types';
import { useSmartLinkAnalyticsContext } from './SmartLinkAnalyticsContext';

interface Props {
  url: string;
  plainLink: () => JSX.Element;
  containers?: object;
}

type ExpandedSmartLinkProps = Props & Partial<SmartLinkAnalyticsContextType>;

const withErrorBoundary = (
  Component: React.FunctionComponent<ExpandedSmartLinkProps>,
) => (props: ExpandedSmartLinkProps) => {
  return (
    <ErrorBoundary
      tags={{
        ownershipArea: 'trello-workflowers',
        feature: Feature.SmartLink,
      }}
      errorHandlerComponent={props.plainLink}
    >
      <Component {...props} />
    </ErrorBoundary>
  );
};

export const SmartLink = withErrorBoundary((props: ExpandedSmartLinkProps) => {
  const analyticsContext = useSmartLinkAnalyticsContext();
  const { source, attributes } = analyticsContext;
  const { source: injectedSource, containers } = props;
  const [shouldRender, setShouldRender] = useState(false);
  const [resolvedUrl, setResolvedUrl] = useState<ResolveResponse | undefined>();
  const [authorizedProviders] = useSharedState(
    smartCardClient.authorizedProviders,
  );

  // This is hacky way to re-render the Smart Cards after the user has completed the authorization
  // flow for a provider. This can go away when @atlaskit/smart-card supports a singleton store prop
  const [akSmartCardProviderKey, setAkSmartCardProviderKey] = useState(
    Date.now(),
  );

  useEffect(() => {
    const definitionId = resolvedUrl?.meta?.definitionId;
    if (definitionId && authorizedProviders.has(definitionId)) {
      setAkSmartCardProviderKey(Date.now());
    }
  }, [authorizedProviders, resolvedUrl]);

  const smartLinkAttAnalytics = useMemo(
    () => ({
      source: injectedSource ? injectedSource : source,
      attributes: {
        component: 'smartLink',
        definitionId: resolvedUrl?.meta?.definitionId,
        accessType: resolvedUrl?.meta?.requestAccess?.accessType,
        cloudId: resolvedUrl?.meta?.requestAccess?.cloudId,
        urlType: resolvedUrl?.data?.generator?.name,
        urlAccess: resolvedUrl?.meta?.access,
        ...(attributes && attributes),
        containers,
      },
    }),
    [resolvedUrl, attributes, source, injectedSource, containers],
  );

  const onLinkResolved = (resolved: ResolveResponse) => {
    setResolvedUrl(resolved);
    setShouldRender(true);
  };

  const onLinkUnresolved = () => {
    setResolvedUrl(undefined);
    setShouldRender(false);
  };

  const onSmartCardClick = useCallback(() => {
    Analytics.sendClickedLinkEvent({
      linkName: 'smartLink',
      ...smartLinkAttAnalytics,
    });
  }, [smartLinkAttAnalytics]);

  return (
    <AkSmartCardWithProvider
      akSmartCardProps={{
        appearance: 'inline',
        inheritDimensions: true,
        showActions: false,
      }}
      onLinkResolved={onLinkResolved}
      onLinkUnresolved={onLinkUnresolved}
      shouldRender={shouldRender}
      onSmartCardClick={onSmartCardClick}
      analyticsContextData={smartLinkAttAnalytics}
      akSmartCardProviderKey={akSmartCardProviderKey}
      {...props}
    />
  );
});
